import React, { FC } from 'react'

import { ProgressBar } from '../ProgressBar'
import * as S from './RatingBar.styles'
import {
  getFormattedRating,
  getNPSRatingColor,
  getPercentageOf,
  getRatingColor,
  getSubRatingColor,
} from './RatingBar.utils'

interface RatingTypeProps {
  isNPS?: boolean
  isSubRating?: boolean
}

export interface RatingBarProps extends RatingTypeProps {
  isOverall?: boolean
  rating?: number
  shouldDisplayTwoDecimals?: boolean
  shouldHideProgressBar?: boolean
  shouldRenderRatingAsMediumSize?: boolean
  shouldShowMaxRating?: boolean
  type?: 'default' | 'points'
}

const MAX_OVERALL_RATING = 5
const MAX_NPS_RATING = 10

const getColorByRatingType = ({
  rating,
  isNPS,
  isSubRating,
}: RatingTypeProps & { rating: number }) => {
  if (isNPS) {
    return getNPSRatingColor(rating)
  }

  if (isSubRating) {
    return getSubRatingColor(rating)
  }

  return getRatingColor(rating)
}

export const RatingBar: FC<RatingBarProps> = ({
  isNPS,
  isSubRating,
  isOverall = false,
  rating,
  shouldDisplayTwoDecimals,
  shouldHideProgressBar,
  shouldRenderRatingAsMediumSize,
  shouldShowMaxRating,
  type = 'default',
}) => {
  const maxRatingValue = isNPS ? MAX_NPS_RATING : MAX_OVERALL_RATING
  const formattedRating = getFormattedRating({ rating, shouldDisplayTwoDecimals })
  const ratingNumber = Number(formattedRating)
  const ratingToDisplay = rating != null ? formattedRating : '-'

  if (shouldHideProgressBar) {
    const ratingColor = getColorByRatingType({ rating: ratingNumber, isSubRating, isNPS })

    return (
      <S.RatingValue $isMediumSize={shouldRenderRatingAsMediumSize} color={ratingColor}>
        {ratingToDisplay}
        {shouldShowMaxRating ? `/${maxRatingValue}` : null}
      </S.RatingValue>
    )
  }

  const progressBarColor = isNPS ? getNPSRatingColor(ratingNumber) : getRatingColor(ratingNumber)

  return (
    <S.RatingBar>
      <ProgressBar
        color={progressBarColor}
        percentage={rating ? getPercentageOf(rating, maxRatingValue) : 0}
        type={type}
      />
      <S.RatingValue $isMediumSize={isOverall} color={progressBarColor}>
        {ratingToDisplay}
        {shouldShowMaxRating ? `/${maxRatingValue}` : null}
      </S.RatingValue>
    </S.RatingBar>
  )
}
